import { BaseRes, PageQuery } from 'types/base';
import {
  SysDict,
  SysUser,
  SysRole,
  SysMenu,
  SysRoleResourcesCategoryList,
  SysRoleResourceRelations,
  SysAdminRole,
  SysRoleResourcesCategory,
  SysRoleResources,
  AdminPermissions,
} from 'types/sys';
import request, { ginzaxiaomaApiRequest } from '.';

export const getUmsAdminList = () => {
  return ginzaxiaomaApiRequest.get<
    {
      id: number;
      username: string;
      nickName: string;
      shop?: number;
      subShop?: number[];
      status: number;
    }[]
  >('/admin/common/ums-admin-list');
};

type GetSysUserListDto = {
  keyword?: string;
} & PageQuery;

export const getSysUserList = (data: GetSysUserListDto) => {
  return request.get<BaseRes<{ list: SysUser[]; total: number }>>(
    '/sys/user/list',
    { params: data },
  );
};

type SysUserRegisterDto = {
  username: SysUser['username'];
  nickName: SysUser['nickName'];
  password: SysUser['password'];
  email: SysUser['email'];
  note: SysUser['note'];
  shop: SysUser['shop'];
};

export const sysUserRegister = (data: SysUserRegisterDto) => {
  return request.post('/sys/user/register', data);
};

export const getSysDictList = async (): Promise<{ records: SysDict[] }> => {
  const { list } = await ginzaxiaomaApiRequest.post<{
    list: {
      id: string;
      type: string;
      valueList: string;
      status: number;
      remark: string;
    }[];
    total: number;
  }>('/admin/sys-dict/list', {
    pageNum: 1,
    pageSize: 9999,
  });

  return {
    records: list.map((item) => ({
      id: Number(item.id),
      type: item.type,
      status: item.status,
      remark: item.remark,
      valueList: JSON.parse(item.valueList),
    })),
  };
};

export const getRoleList = () => {
  return ginzaxiaomaApiRequest.get<SysRole[]>('admin/role/list');
};

export const roleEdit = (data: SysRole) => {
  return ginzaxiaomaApiRequest.post('admin/role/edit', data);
};

export const roleDelete = (id: SysRole['id']) => {
  return ginzaxiaomaApiRequest.delete(`admin/role/delete/${id}`);
};

export const getMenuList = () => {
  return ginzaxiaomaApiRequest.get<SysMenu[]>('admin/menu/list');
};

export const menuEdit = (data: SysMenu) => {
  return ginzaxiaomaApiRequest.post('admin/menu/edit', data);
};

export const menuDelete = (id: SysMenu['id']) => {
  return ginzaxiaomaApiRequest.delete(`admin/menu/delete/${id}`);
};

export const getRoleMenuRelations = (id: SysRole['id']) => {
  return ginzaxiaomaApiRequest.get<
    {
      menuId: string;
      id: number;
      roleId: number;
    }[]
  >(`admin/role/relations/${id}`);
};

export const updateRoleMenuRelations = (dto: {
  roleId: number;
  menuIds: number[];
}) => {
  return ginzaxiaomaApiRequest.post(
    `admin/role/update-role-menu-relations`,
    dto,
  );
};

export const getRoleResources = () => {
  return ginzaxiaomaApiRequest.get<SysRoleResourcesCategoryList[]>(
    `admin/role/role-resources`,
  );
};

export const getRoleResourceRelations = (id: SysRole['id']) => {
  return ginzaxiaomaApiRequest.get<SysRoleResourceRelations[]>(
    `admin/role/role-resource-relations/${id}`,
  );
};

export const updateRoleResourceRelations = (dto: {
  roleId: string;
  resources: string[];
}) => {
  return ginzaxiaomaApiRequest.post(
    `admin/role/update-role-resource-relations`,
    dto,
  );
};

export const getAdminRoles = (id: number) => {
  return ginzaxiaomaApiRequest.get<SysAdminRole[]>(
    `admin/role/admin-roles/${id}`,
  );
};

export const updateAdminRoles = (dto: { adminId: number; roles: number[] }) => {
  return ginzaxiaomaApiRequest.post(
    `admin/role/update-admin-role-relations`,
    dto,
  );
};

export const getAdminPermissions = (id: number) => {
  return ginzaxiaomaApiRequest.get<AdminPermissions>(
    `admin/ums-admin/${id}/permissions`,
  );
};

export const updateAdminDirectPermissions = (dto: {
  adminId: number;
  menuIds: number[];
  resourceIds: number[];
}) => {
  return ginzaxiaomaApiRequest.post(
    'admin/ums-admin/update-direct-permissions',
    dto,
  );
};

export const getResourceCategoryList = () => {
  return ginzaxiaomaApiRequest.get<SysRoleResourcesCategory[]>(
    `admin/role/resource-category-list`,
  );
};

export const resourceCategoryDelete = (id: SysMenu['id']) => {
  return ginzaxiaomaApiRequest.delete(
    `admin/role/resource-category-delete/${id}`,
  );
};

export const resourceCategoryEdit = (dto: SysRoleResourcesCategory) => {
  return ginzaxiaomaApiRequest.post(`admin/role/edit-resource-category`, dto);
};

export const getResourceList = (dto: {
  keyword?: string;
  categoryIdList?: string;
  pageNum: number;
  pageSize: number;
}) => {
  return ginzaxiaomaApiRequest.get<{
    list: SysRoleResources[];
    total: number;
  }>(`admin/role/resource-list`, {
    params: dto,
  });
};

export const resourceEdit = (data: SysRoleResources) => {
  return ginzaxiaomaApiRequest.post('admin/role/resource-edit', data);
};

export const resourceDelete = (id: SysRoleResources['id']) => {
  return ginzaxiaomaApiRequest.delete(`admin/role/resource-delete/${id}`);
};

export const sysDictApi = {
  list: (dto: { pageNum: number; pageSize: number }) => {
    return ginzaxiaomaApiRequest.post<{
      list: {
        id: string;
        type: string;
        valueList: string;
        status: number;
        remark: string;
      }[];
      total: number;
    }>('/admin/sys-dict/list', dto);
  },
  add: (dto: { type: string; valueList: string; remark?: string }) => {
    return ginzaxiaomaApiRequest.post('/admin/sys-dict/add', dto);
  },
  update: (dto: {
    id: string;
    type: string;
    valueList: string;
    remark?: string;
  }) => {
    return ginzaxiaomaApiRequest.put(`/admin/sys-dict/`, dto);
  },
};

export const getUmsAdminListV2 = (
  dto: {
    keyword?: string;
    status?: number;
    shop?: number;
    isLocked?: boolean;
    sortByLoginAttempts?: 'ASC' | 'DESC';
  } & PageQuery,
) => {
  return ginzaxiaomaApiRequest.get<{
    list: {
      id: number;
      username: string;
      nickName: string;
      shop: number | null;
      subShop: number[] | null;
      status: number;
      createTime: string;
      loginTime?: string;
      email?: string;
      note?: string;
      icon?: string;
      isLocked?: boolean;
      lockUntil?: string | null;
      loginAttempts?: number;
      recentLoginLog: {
        id: number;
        createTime?: string;
        ip?: string;
        userAgent?: string;
        address?: string;
      } | null;
    }[];
    total: number;
  }>('/admin/ums-admin/list', { params: dto });
};

export const updateUmsAdmin = (dto: {
  id: number;
  status?: number;
  nickName?: string;
  email?: string;
  note?: string;
  shop?: number | null;
  subShop?: number[];
}) => {
  return ginzaxiaomaApiRequest.post('/admin/ums-admin/update', dto);
};

export const updateUmsAdminPassword = (dto: {
  id: number;
  password: string;
}) => {
  return ginzaxiaomaApiRequest.post('/admin/ums-admin/update-password', dto);
};

export const unlockUmsAdmin = (id: number) => {
  return ginzaxiaomaApiRequest.post(`/admin/ums-admin/unlock/${id}`);
};

export const getUmsAdminLoginLog = (dto: {
  pageNum: number;
  pageSize: number;
  adminId?: number | string;
  username?: string;
  success?: boolean;
  ip?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return ginzaxiaomaApiRequest.get<{
    list: {
      id: number;
      adminId?: string | null;
      username?: string | null;
      success?: boolean;
      createTime?: string;
      ip?: string;
      userAgent?: string;
      address?: string;
    }[];
    total: number;
  }>('/admin/ums-admin/login-log', { params: dto });
};

export const getFileUploadLogDailyStats = (dto?: {
  days?: 7 | 14 | 30;
  timeZone?: string;
}) => {
  return ginzaxiaomaApiRequest.get<{
    days: 7 | 14 | 30;
    list: {
      date: string;
      count: number;
      totalSize: number;
    }[];
  }>('/admin/file-upload-log/daily-stats', { params: dto });
};

export const getFileUploadLogList = (dto: {
  pageNum: number;
  pageSize: number;
  operatorType?: 'member' | 'admin' | 'system';
  operatorId?: number;
  operatorName?: string;
  source?: string;
  mimeType?: string;
  isPrivate?: 0 | 1;
  startTime?: string;
  endTime?: string;
  originalName?: string;
  timeZone?: string;
}) => {
  return ginzaxiaomaApiRequest.get<{
    list: {
      id: string;
      createdAt: string;
      operatorType: 'member' | 'admin' | 'system';
      operatorId: number | null;
      operatorName: string | null;
      source: string;
      originalName: string | null;
      mimeType: string | null;
      size: number | null;
      s3Key: string;
      url: string;
      isPrivate: 0 | 1;
      ip: string | null;
      address: string | null;
      userAgent: string | null;
    }[];
    total: number;
  }>('/admin/file-upload-log/list', { params: dto });
};

export const ipBlacklistApi = {
  list: (dto: { pageNum: number; pageSize: number; ip?: string }) => {
    return ginzaxiaomaApiRequest.post<{
      list: {
        id: string;
        ip: string;
        remark?: string | null;
        createdBy?: string | null;
        createTime?: string;
      }[];
      total: number;
    }>('/admin/ip-blacklist/list', dto);
  },
  add: (dto: { ip: string; remark?: string }) => {
    return ginzaxiaomaApiRequest.post('/admin/ip-blacklist/add', dto);
  },
  remove: (dto: { id?: string; ip?: string }) => {
    return ginzaxiaomaApiRequest.post('/admin/ip-blacklist/remove', dto);
  },
};
